import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaHome, FaMap, FaBuilding, FaRoute, FaInfoCircle, FaCog } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../images/logo1.png';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // start with null so "Home" isn't marked active before we detect any section
  const [activeSection, setActiveSection] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Page configuration
  const pageConfig = {
    '/': { title: 'Home', icon: <FaHome /> },
    '/events': { title: 'Events', icon: <FaMap /> },
    '/event': { title: 'Event Details', icon: <FaInfoCircle /> },
    '/gallery': { title: 'Gallery', icon: <FaBuilding /> },
    '/about': { title: 'About', icon: <FaInfoCircle /> },
    '/contact': { title: 'Contact', icon: <FaRoute /> }
  };

  const currentPage = pageConfig[location.pathname] || 
                     (location.pathname.startsWith('/event/') ? pageConfig['/event'] : 
                     { title: 'Nengai Foundation', icon: <FaHome /> });

  // Navigation items
  const navItems = [
    { path: '/#home', label: 'Home', icon: <FaHome />, hash: 'home' },
    { path: '/#about', label: 'About', icon: <FaInfoCircle />, hash: 'about' },
    { path: '/#aims', label: 'Aims', icon: <FaBuilding />, hash: 'aims' },
    { path: '/#outcomes', label: 'Outcomes', icon: <FaRoute />, hash: 'outcomes' },
    { path: '/events', label: 'Events', icon: <FaMap />, hash: null },
    { path: '/#gallery', label: 'Gallery', icon: <FaBuilding />, hash: 'gallery' },
    { path: '/#contact', label: 'Contact', icon: <FaInfoCircle />, hash: 'contact' }
  ];

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set active section based on scroll position (for home page)
  useEffect(() => {
    const handleScrollActive = () => {
      if (location.pathname !== '/') return;

      const headerEl = document.querySelector('header');
      const headerHeight = headerEl ? headerEl.offsetHeight : 80;

      const sections = Array.from(document.querySelectorAll('section[id]'));
      if (!sections.length) {
        setActiveSection(null);
        return;
      }

      const scrollPosition = window.scrollY + headerHeight + 5;
      let current = null;

      sections.forEach(section => {
        const top = section.offsetTop;
        const height = section.offsetHeight;
        const id = section.getAttribute('id');
        if (scrollPosition >= top && scrollPosition < top + height) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    if (location.pathname === '/') {
      window.addEventListener('scroll', handleScrollActive);
      window.addEventListener('resize', handleScrollActive);
      // Initial check after render
      setTimeout(handleScrollActive, 50);
    } else {
      setActiveSection(null);
    }

    return () => {
      window.removeEventListener('scroll', handleScrollActive);
      window.removeEventListener('resize', handleScrollActive);
    };
  }, [location.pathname]);

  // Close menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileMenuOpen]);

  // Handle navigation clicks
  const handleNavClick = (path, hash = null) => {
    if (path === '/') {
      navigate('/');
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            const headerEl = document.querySelector('header');
            const headerHeight = headerEl ? headerEl.offsetHeight : 80;
            const targetTop = element.getBoundingClientRect().top + window.scrollY - headerHeight + 1;
            window.scrollTo({ top: targetTop, behavior: 'smooth' });
            // set active after a short delay to allow smooth scroll
            setTimeout(() => setActiveSection(hash), 300);
          }
        }, 100);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // mark top/home after scroll
        setTimeout(() => setActiveSection('home'), 200);
      }
    } else if (path.startsWith('/#')) {
      const hash = path.split('#')[1];
      const scrollToHash = () => {
        const element = document.getElementById(hash);
        if (!element) return;
        const headerEl = document.querySelector('header');
        const headerHeight = headerEl ? headerEl.offsetHeight : 80;
        const targetTop = element.getBoundingClientRect().top + window.scrollY - headerHeight + 1;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
        setTimeout(() => setActiveSection(hash), 300);
      };

      if (location.pathname === '/') {
        scrollToHash();
      } else {
        navigate('/');
        setTimeout(scrollToHash, 120);
      }
    } else {
      navigate(path);
    }
    closeMobileMenu();
  };

  // Check if a nav item is active
  const isActive = (item) => {
  // For full page routes (like /events)
  if (item.path === location.pathname) {
    return true;
  }

  // For events and event details
  if (item.path === '/events' && location.pathname.startsWith('/event')) {
    return true;
  }

  // For hash links on home page
  if (item.path.startsWith('/#') && location.pathname === '/') {
    const hash = item.path.split('#')[1];
    return activeSection === hash;
  }

  // FIX: Home should be active only when home is the active section
  if (item.path === '/' && location.pathname === '/') {
    return activeSection === 'home';
  }

  return false;
};


  // Handle donate button click
  const handleDonateClick = () => {
    window.open('https://store.pesapal.com/donatetonengaifoundation', '_blank');
  };

  return (
    <>
      <header className={scrolled ? 'scrolled' : ''}>
        <div className="container">
          {/* Desktop Header */}
          <div className="header-content desktop-header">
            <div className="logo-section">
              <div className="logo" onClick={() => handleNavClick('/')}>
                <div className="logo-icon">
                  <img src={logoImage} alt="Nengai Foundation Logo" />
                </div>
                
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <ul>
                {navItems.map((item) => (
                  <li key={item.path}>
                    <a
                      href={item.path}
                      className={isActive(item) ? 'active' : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.path, item.hash);
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            
            {/* Desktop Donate Button */}
            <div className="admin-button">
              <button className="btn-admin" onClick={handleDonateClick}>
                <FaCog />
                Donate
              </button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="header-content mobile-header">
            <div className="mobile-top-bar">
              <div className="mobile-logo">
                <div className="mobile-logo-icon">
                  <img src={logoImage} alt="Nengai Foundation Logo" />
                </div>
                <div className="mobile-page-title">
                  {currentPage.icon}
                  <span>{currentPage.title}</span>
                </div>
              </div>
              
              <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                <FaBars />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <div className={`mobile-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>
      
      <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-header">
          <div className="mobile-nav-logo">
            <div className="mobile-nav-logo-icon">
              <img src={logoImage} alt="Nengai Foundation Logo" />
            </div>
            <h2>Nengai Foundation</h2>
          </div>
          <button className="close-menu" onClick={closeMobileMenu}>
            <FaTimes />
          </button>
        </div>
        
        <div className="mobile-nav-links">
          {navItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              className={isActive(item) ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.path, item.hash);
              }}
            >
              {item.icon}
              {item.label}
            </a>
          ))}
        </div>
        
        <div className="mobile-admin-section">
          <button className="mobile-admin-btn" onClick={handleDonateClick}>
            <FaCog />
            Donate Now
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;